/**
 * Diskriminanten und Fehlertyp für die Crypto-Pipeline (AFO-007 / DEV-013).
 *
 * Werte werden in AFO-009/013 auf den Technical-Error-Screen Variante B
 * gemappt. Sie bleiben getrennt, damit Telemetrie (AFO-010) pro Sub-Code
 * zählt und das Debugging in der Pilotphase eine genaue Ursache hat.
 *
 * - `MISSING_KEY`: Kein non-extractable Patient-Privatkey verfügbar
 *   (Reload-Recovery hat keinen IDB-Eintrag gefunden oder Submit lief nicht).
 * - `DECRYPT_FAILED`: AES-GCM-Decrypt oder RSA-OAEP-Unwrap warf — typischerweise
 *   korruptes Chiffrat, falscher AAD oder falsche IV.
 * - `INVALID_FORMAT`: Klartext lässt sich nicht als JSON-`TenantResponsePayload`
 *   parsen / die Schema-Prüfung fällt durch.
 * - `RECOVERY_EXPIRED`: IDB-Eintrag älter als `expiresAt` — der Job wurde
 *   verworfen und IDB cleanup ausgelöst.
 */
export const CryptoErrorKind = {
  MissingKey: 'MISSING_KEY',
  DecryptFailed: 'DECRYPT_FAILED',
  InvalidFormat: 'INVALID_FORMAT',
  RecoveryExpired: 'RECOVERY_EXPIRED',
} as const;

export type CryptoErrorKind = (typeof CryptoErrorKind)[keyof typeof CryptoErrorKind];

interface CryptoErrorOptions {
  readonly kind: CryptoErrorKind;
  readonly cause?: unknown;
}

/**
 * Typisierter Fehler der Crypto-Pipeline.
 *
 * Aufrufer prüfen die Diskriminante via `error.kind` und ignorieren die
 * (englischsprachige) Message für UI-Texte — diese liefert AFO-016.
 */
export class CryptoError extends Error {
  public readonly kind: CryptoErrorKind;

  public constructor(options: CryptoErrorOptions) {
    super(`CryptoError: ${options.kind}`);
    this.name = 'CryptoError';
    this.kind = options.kind;
    if (options.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}
